import { html, css, nothing } from 'lit';
import type { PropertyValues } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { msg, localized } from '@lit/localize';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { SsoElement } from '../components/sso-base.js';
import { endpoints } from '../api/client.js';
import { navigate } from '../router.js';
import type { TotpEnrollResult } from '../api/types.js';
import '../components/sso-card.js';
import '../components/sso-code-input.js';
import '../components/sso-button.js';
import '../components/sso-alert.js';
import '../components/sso-spinner.js';

/**
 * `<totp-enroll-page>` — set up an authenticator app (SSO-MOCKUPS.md §4, SSO-FRONTEND-LIT.md §4.4).
 *
 * Renders the `otpauth://` QR client-side (keeps the secret off any image cache), shows the manual
 * secret with a copy button, and verifies a first code to activate. The secret is held transiently
 * and discarded the moment activation succeeds — never stored.
 */
@customElement('totp-enroll-page')
@localized()
export class TotpEnrollPage extends SsoElement {
  static override styles = [
    SsoElement.styles,
    css`
      .qr {
        display: block;
        width: 200px;
        height: 200px;
        margin: var(--sso-space) auto;
        border: 1px solid var(--sso-color-border);
        border-radius: var(--sso-radius);
        background: #fff;
        padding: 8px;
        box-sizing: content-box;
      }
      .qr svg {
        display: block;
        width: 100%;
        height: 100%;
      }
      .secret-row {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }
      .secret {
        flex: 1;
        font: var(--sso-font-mono);
        letter-spacing: 0.15em;
        word-break: break-all;
        background: color-mix(in srgb, var(--sso-color-brand) 8%, transparent);
        padding: 0.6rem 0.75rem;
        border-radius: var(--sso-radius);
      }
      .copy {
        flex: none;
        background: none;
        border: 1px solid var(--sso-color-border);
        border-radius: var(--sso-radius);
        min-height: var(--sso-touch-target);
        padding: 0 0.75rem;
        cursor: pointer;
        color: var(--sso-color-brand);
        font: inherit;
      }
      .copy:focus-visible {
        outline: none;
        box-shadow: var(--sso-focus-ring);
      }
      ol {
        padding-inline-start: 1.2rem;
        color: var(--sso-color-fg);
      }
      li {
        margin-bottom: 0.4rem;
      }
      sso-code-input {
        margin: var(--sso-space) 0;
      }
      .actions {
        display: flex;
        gap: var(--sso-space);
      }
      .actions sso-button {
        flex: 1;
      }
    `,
  ];

  @state() private begin?: TotpEnrollResult;
  @state() private error = '';
  @state() private busy = false;
  @state() private copied = false;

  override async connectedCallback(): Promise<void> {
    super.connectedCallback();
    try {
      // Server generates the secret (held pending confirm) and returns the QR SVG + manual key.
      this.begin = await endpoints.mfaTotpEnroll();
    } catch {
      this.error = msg('Could not start authenticator setup. Please try again.');
    }
  }

  override updated(changed: PropertyValues): void {
    super.updated(changed);
    // Content loads asynchronously — focus the code-entry boxes once they have rendered.
    this.focusFirstControl();
  }

  private async copySecret(): Promise<void> {
    if (!this.begin) return;
    try {
      await navigator.clipboard.writeText(this.begin.manualKey);
      this.copied = true;
      window.setTimeout(() => (this.copied = false), 2000);
    } catch {
      /* clipboard may be blocked; the secret is visible as text anyway */
    }
  }

  private async activate(code: string): Promise<void> {
    this.busy = true;
    this.error = '';
    try {
      const r = await endpoints.mfaTotpConfirm(code);
      this.begin = undefined; // discard the secret from memory immediately
      // First successful 2FA enrollment returns one-time recovery codes → show them once.
      if (r.recoveryCodes && r.recoveryCodes.length) {
        this.flowContext?.setRecoveryCodes(r.recoveryCodes);
        navigate('/recovery-codes');
        return;
      }
      const returnUri = this.resolveReturnUri();
      if (returnUri) { this.redirect(returnUri); return; }
      navigate('/');
    } catch {
      this.error = msg('That code was not accepted. Please try again.');
    } finally {
      this.busy = false;
    }
  }

  private cancel(): void {
    // Within a forced gate, cancel means sign out; otherwise return to the launcher.
    navigate('/');
  }

  override render() {
    if (!this.begin && !this.error) {
      return html`<sso-card><sso-spinner aria-label=${msg('Loading')}></sso-spinner></sso-card>`;
    }
    return html`
      <sso-card>
        <h1>${msg('Set up your authenticator app')}</h1>
        ${this.error ? html`<sso-alert kind="error">${this.error}</sso-alert>` : nothing}
        ${this.begin
          ? html`
              <ol>
                <li>
                  ${msg(
                    'Scan this QR code with your authenticator app (Microsoft Authenticator, Google Authenticator, Authy, …).',
                  )}
                </li>
              </ol>
              <div class="qr" role="img" aria-label=${msg('Authenticator setup QR code')}>
                ${unsafeSVG(this.begin.qrSvg)}
              </div>
              <p>${msg('Or enter this key manually:')}</p>
              <div class="secret-row">
                <code class="secret">${this.begin.manualKey}</code>
                <button
                  type="button"
                  class="copy"
                  @click=${this.copySecret}
                  aria-label=${msg('Copy secret')}>
                  ${this.copied ? msg('Copied') : msg('Copy')}
                </button>
              </div>
              <ol start="2">
                <li>${msg('Enter the 6-digit code to confirm.')}</li>
              </ol>
              <sso-code-input
                length="6"
                ?disabled=${this.busy}
                aria-label=${msg('6-digit code from your authenticator app')}
                @complete=${(e: CustomEvent<string>) => this.activate(e.detail)}></sso-code-input>
              <div class="actions">
                <sso-button variant="secondary" ?disabled=${this.busy} @click=${this.cancel}>
                  ${msg('Cancel')}
                </sso-button>
              </div>
            `
          : nothing}
      </sso-card>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'totp-enroll-page': TotpEnrollPage;
  }
}
